import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import { FaPlus, FaMinus } from 'react-icons/fa';
import LandingNavbar from './Navbar';
import LandingFooter from './LandingFooter';
import ScrollReveal from './ScrollReveal';
import { useDemoModal } from './DemoModal';
import {
  PRICE_HERO, PLANS, HOW, ESTIMATOR, COMPARE, ADDONS, CHOOSE, PILOT, PRICE_FAQS, PRICE_CTA,
} from './pricingContent';
import './landing-animations.css';
import './landing-theme.css';
import './landing-eb.css';
import './landing-eb-pricing.css';

// Brand icons: src/assets/images/eb/icons/<name>-<green|cream|gold>.png
const iconCtx = require.context('../../assets/images/eb/icons', false, /\.png$/);
const icon = (name, tone = 'green') => iconCtx(`./${name}-${tone}.png`);

const fmt = (n) => `$${n.toLocaleString('en-US')}`;

// Dedicated trial sign-up route. Leave null until it exists; until then the trial
// button opens the enquiry form (same /contact-messages API) with the trial pre-selected.
// e.g. const TRIAL_ROUTE = '/signup?plan=trial';
const TRIAL_ROUTE = null;

// A plan CTA counts as a trial action when its label mentions "free" or "trial".
const isTrialCta = (label = '') => /free|trial/i.test(label);

const PricingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openDemo } = useDemoModal(); // "Book a demo" opens the demo request popup
  const [users, setUsers] = useState(ESTIMATOR.initial);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    if (!location.hash) return undefined;
    const t = window.setTimeout(() => {
      document.querySelector(location.hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
    return () => window.clearTimeout(t);
  }, [location.hash]);

  // Book a demo → popup
  const goDemo = () => openDemo();

  // Talk to us → enquiry form, topic "Pricing and plans", message pre-filled
  const goTalk = () => navigate('/enquiry?intent=talk&from=pricing');

  // Start free for 30 days → trial sign-up route, or the enquiry form until it exists
  const goTrial = () => {
    if (TRIAL_ROUTE) navigate(TRIAL_ROUTE);
    else navigate('/enquiry?intent=trial&from=pricing');
  };

  // Plan cards: trial CTA → trial, Enterprise (custom price, no rate) → talk, rest → demo popup
  const onPlanCta = (plan) => {
    if (isTrialCta(plan.cta)) return goTrial();
    if (!plan.rate) return goTalk();
    return goDemo();
  };

  const estimate = PLANS.map((p) => (p.rate
    ? {
      name: p.name,
      cost: fmt(Math.max(users, p.minU) * p.rate),
      note: users < p.minU ? `Billed at the ${p.minU}-user minimum` : `${users} × $${p.rate}`,
    }
    : { name: p.name, cost: 'Custom', note: ESTIMATOR.enterpriseNote }));

  return (
    <div className="landing-page eb min-vh-100 d-flex flex-column">
      <ScrollReveal />
      <LandingNavbar />

      {/* ── Hero + plan cards ── */}
      <header className="eb-pr-hero">
        <div className="eb-pr-hero-inner">
          <div className="eb-pr-hero-copy">
            <p className="eb-eyebrow mb-0">{PRICE_HERO.eyebrow}</p>
            <h1 className="eb-pr-h1">{PRICE_HERO.title}</h1>
            <p className="eb-lead mb-0">{PRICE_HERO.subtitle}</p>
            <span className="eb-pr-reviewed">{PRICE_HERO.reviewed}</span>
          </div>

          <div className="eb-pr-plans">
            {PLANS.map((p) => {
              const dark = !!p.popular;
              const tone = dark ? 'gold' : 'green';
              return (
                <div key={p.name} className={`eb-pr-plan${dark ? ' is-popular' : ''}`}>
                  <div className="eb-pr-plan-top">
                    <span className="name">{p.name}</span>
                    {dark && <span className="eb-pr-badge">Most popular</span>}
                  </div>
                  <span className="tag">{p.tagline}</span>
                  <div className="eb-pr-price">
                    <span className="amt">{p.price}</span>
                    <span className="unit">{p.unit}</span>
                  </div>
                  <span className="min">{p.min}</span>
                  <button type="button" className="eb-pr-plan-cta" onClick={() => onPlanCta(p)}>{p.cta}</button>
                  <ul className="eb-pr-feats">
                    {p.feats.map((f) => (
                      <li key={f}><img src={icon('check', tone)} alt="" /><span>{f}</span></li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>

          <p className="eb-pr-note">{PRICE_HERO.note}</p>
        </div>
      </header>

      {/* ── How pricing works + estimator ── */}
      <section className="eb-section">
        <Container className="eb-pr-est">
          <div className="eb-pr-how">
            <p className="eb-eyebrow mb-0">{HOW.eyebrow}</p>
            <h2 className="eb-h2 reveal reveal-up">{HOW.title}</h2>
            {HOW.items.map((h, i) => (
              <div key={h.t} className="eb-pr-how-item reveal reveal-up">
                <span className="n">0{i + 1}</span>
                <span><strong>{h.t}</strong> <span className="d">{h.d}</span></span>
              </div>
            ))}
          </div>

          <div className="eb-pr-calc reveal reveal-up">
            <span className="eb-pr-calc-title">{ESTIMATOR.title}</span>
            <label className="eb-pr-calc-field" htmlFor="eb-pr-users">
              {ESTIMATOR.label}
              <span className="val">{users.toLocaleString('en-US')}</span>
              <input
                id="eb-pr-users"
                type="range"
                min={ESTIMATOR.min}
                max={ESTIMATOR.max}
                step={ESTIMATOR.step}
                value={users}
                onChange={(e) => setUsers(Number(e.target.value))}
              />
            </label>
            <div className="eb-pr-calc-rows">
              {estimate.map((e) => (
                <div key={e.name} className="eb-pr-calc-row">
                  <div><strong>{e.name}</strong><span>{e.note}</span></div>
                  <b>{e.cost}</b>
                </div>
              ))}
            </div>
            <span className="eb-pr-calc-fine">{ESTIMATOR.disclaimer}</span>
          </div>
        </Container>
      </section>

      {/* ── Compare ── */}
      <section className="eb-section eb-section-cream">
        <Container className="eb-pr-compare">
          <div className="eb-pr-center reveal reveal-up">
            <h2 className="eb-h2">{COMPARE.title}</h2>
            <p className="eb-lead mb-0">{COMPARE.subtitle}</p>
          </div>
          <div className="eb-pr-table-wrap reveal reveal-up">
            <table className="eb-pr-table">
              <thead>
                <tr>
                  <th scope="col" className="feature">Feature</th>
                  {COMPARE.columns.map((c, i) => (
                    <th key={c} scope="col" className={i === COMPARE.highlight ? 'is-hl' : ''}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COMPARE.rows.map(([k, ...vals]) => (
                  <tr key={k}>
                    <th scope="row">{k}</th>
                    {vals.map((v, i) => (
                      <td
                        key={`${k}-${i}`}
                        className={`${i === COMPARE.highlight ? 'is-hl' : ''}${v === 'Add-on' ? ' is-addon' : ''}`}
                      >
                        {v}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="eb-pr-foot">{COMPARE.footnote}</p>
        </Container>
      </section>

      {/* ── Add-ons ── */}
      <section className="eb-section">
        <Container className="eb-pr-addons">
          <div className="eb-pr-center reveal reveal-up">
            <p className="eb-eyebrow mb-0">{ADDONS.eyebrow}</p>
            <h2 className="eb-h2">{ADDONS.title}</h2>
            <p className="eb-lead mb-0">{ADDONS.subtitle}</p>
          </div>
          <div className="eb-pr-addon-grid">
            {ADDONS.items.map((a) => (
              <div key={a.k} className="eb-pr-addon reveal reveal-up">
                <strong>{a.k}</strong>
                <span>{a.d}</span>
              </div>
            ))}
            <div className="eb-pr-addon is-dark reveal reveal-up">
              <strong>{ADDONS.services.k}</strong>
              <span>{ADDONS.services.d}</span>
            </div>
          </div>
        </Container>
      </section>

      {/* ── Choose a plan + pilot ── */}
      <section className="eb-section eb-section-cream">
        <Container className="eb-pr-two">
          <div className="eb-pr-col">
            <h2 className="eb-h2 reveal reveal-up">{CHOOSE.title}</h2>
            <p className="eb-lead mb-0">{CHOOSE.text}</p>
            {CHOOSE.items.map((c) => (
              <div key={c.k} className="eb-pr-choose reveal reveal-up">
                <strong>{c.k}</strong> <span>{c.d}</span>
              </div>
            ))}
          </div>
          <div className="eb-pr-col">
            <h2 className="eb-h2 reveal reveal-up">{PILOT.title}</h2>
            <div className="eb-pr-steps">
              {PILOT.steps.map((s, i) => (
                <div key={s.k} className="eb-pr-step reveal reveal-up">
                  <span className="n">{i + 1}</span>
                  <div><strong>{s.k}</strong><span>{s.d}</span></div>
                </div>
              ))}
            </div>
            <p className="eb-pr-links">
              {PILOT.linksBefore}<Link to={PILOT.link1.to}>{PILOT.link1.label}</Link>
              {PILOT.linksMid}<Link to={PILOT.link2.to}>{PILOT.link2.label}</Link>.
            </p>
          </div>
        </Container>
      </section>

      {/* ── FAQ ── */}
      <section id="faq" className="eb-section">
        <Container style={{ maxWidth: 900 }}>
          <h2 className="eb-h2 text-center mb-4 reveal reveal-up">EthosBoard pricing: frequently asked questions</h2>
          <div className="eb-pr-faq-list">
            {PRICE_FAQS.map(({ q, a }, i) => {
              const open = openFaq === i;
              return (
                <div key={q} className="eb-faq-item">
                  <button
                    type="button"
                    className="eb-faq-q"
                    aria-expanded={open}
                    aria-controls={`eb-pr-faq-${i}`}
                    onClick={() => setOpenFaq(open ? -1 : i)}
                  >
                    {q}
                    <span className="eb-faq-sign">{open ? <FaMinus size={12} /> : <FaPlus size={12} />}</span>
                  </button>
                  {open && <div id={`eb-pr-faq-${i}`} className="eb-faq-a">{a}</div>}
                </div>
              );
            })}
          </div>
        </Container>
      </section>

      {/* ── Demo CTA ── */}
      <section id="demo" className="eb-section">
        <Container>
          <div className="eb-cta-card text-center reveal reveal-up">
            <h2 className="eb-h2">{PRICE_CTA.title}</h2>
            <p className="eb-lead">{PRICE_CTA.text}</p>
            <div className="d-flex flex-wrap gap-3 justify-content-center">
              <button type="button" className="landing-btn-primary" onClick={goDemo}>{PRICE_CTA.primary}</button>
              <button type="button" className="landing-btn-secondary" onClick={goDemo}>{PRICE_CTA.secondary}</button>
            </div>
          </div>
        </Container>
      </section>

      <LandingFooter />
    </div>
  );
};

export default PricingPage;
